import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { deptColor, moneyShort, salesDashboard } from '@battleops/shared';
import { useDeptGoals } from '@battleops/client/src/data/useDeptGoals';
import Icon from '../Icon';
import s from '../../pages/Board.module.css';

// Sales' board = the pipeline (plan note, Oct 16): one card per lead, columns New → Contacted → Assessment → Quoted →
// Won · Lost (last 30 days). Cards come from the books' Sales feed, so until the lead-is-job remodel lands a card is a
// PERSON and the stage is set in BattleBids — the board reads, it doesn't drag. Each card: who, open $, days in the
// pipeline (red past the threshold), the ops job it belongs to.

const STAGES = [
  { id: 'new', label: 'New', hint: 'enquiry logged, nobody has talked to them yet' },
  { id: 'contacted', label: 'Contacted', hint: 'first call made, no visit booked' },
  { id: 'assessment', label: 'Assessment', hint: 'site visit booked or done, nothing sent' },
  { id: 'quoted', label: 'Quoted', hint: 'bid sent, waiting on them' },
];
const DAY = 86400000;
const shortDay = (date) => new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const since = (date, today) => (date ? Math.round((new Date(`${today}T12:00:00`) - new Date(`${date}T12:00:00`)) / DAY) : null);

export default function PipelineBoard({ dept }) {
  const navigate = useNavigate();
  const { data } = useDeptGoals(dept);
  const x = useMemo(() => (dept ? salesDashboard(dept, data, { weeks: 8 }) : null), [dept, data]);
  const accent = deptColor(dept);
  const projectFor = (customerId) => (customerId ? (data.projects || []).find((p) => p && !p.deletedAt && p.customerId === customerId) || null : null);
  if (!x) return null;
  if (!x.ready) return <div className="empty" style={{ marginTop: 16 }}>The books’ Sales feed hasn’t reached this device yet — the pipeline shows once it has.</div>;
  const feed = data.sales;
  const today = x.today;
  const openById = new Map(x.pipeline.rows.map((r) => [r.customerId, r]));
  const cols = STAGES.map((st) => {
    const stage = (feed.pipeline?.stages || []).find((q) => q.stage === st.id);
    const rows = (stage?.rows || []).map((r) => ({ ...r, bid: openById.get(r.customerId) || null, days: since(r.since, today), project: projectFor(r.customerId) }))
      .sort((a, b) => (b.days || 0) - (a.days || 0));
    const late = st.id === 'new' ? (r) => r.days >= 1 : st.id === 'quoted' ? (r) => r.bid?.overdue : st.id === 'assessment' ? (r) => r.days > x.targets.turnaroundDays + 7 : () => false;
    return { ...st, kind: 'stage', rows, count: rows.length, value: stage?.value || 0, late };
  });
  const cut = x.weeks[Math.max(0, x.weeks.length - 5)].from;
  const won = x.sold.rows.filter((r) => r.date >= cut).map((r) => ({ name: r.customer, customerId: r.customerId, value: r.value, since: r.date, days: since(r.date, today), project: projectFor(r.customerId) }));
  const lost = x.lost.rows.filter((r) => r.date >= cut).map((r) => ({ name: r.customer, customerId: r.customerId, value: r.value, since: r.date, days: since(r.date, today), reason: r.reason, project: projectFor(r.customerId) }));
  const all = [...cols, { id: 'won', label: 'Won', kind: 'done', rows: won, count: won.length, value: won.reduce((a, r) => a + r.value, 0), late: () => false, hint: 'signed in the last 30 days' }, { id: 'lost', label: 'Lost', kind: 'lost', rows: lost, count: lost.length, value: lost.reduce((a, r) => a + r.value, 0), late: () => false, hint: 'declined or expired, last 30 days' }];
  return (
    <div className={s.page} data-embedded style={{ '--tone': accent }}>
      <div className={s.filters}>
        <span className={s.hint}><Icon name="info" size={13} />A card is a lead from the books · stages change in BattleBids until leads become jobs · {x.pipeline.count} in play, {moneyShort(x.pipeline.value)} open</span>
      </div>
      <div className={s.board}>
        {all.map((col) => {
          const tint = col.kind === 'done' ? 'var(--c-success)' : col.kind === 'lost' ? 'var(--c-danger)' : accent;
          return (
            <section key={col.id} className={s.col} data-kind={col.kind === 'lost' ? 'review' : col.kind} style={{ '--ct': tint }} aria-label={`${col.label}, ${col.count} leads`}>
              <div className={s.colHead}>
                <span className={s.colLabel}>{col.label}</span>
                <span className={s.count}>{col.count}</span>
                <span className="spacer" />
                {col.value ? <span className={s.n}>{moneyShort(col.value)}</span> : null}
              </div>
              <div className={s.cards}>
                {col.rows.map((r) => {
                  const bad = col.late(r);
                  return (
                    <article key={`${col.id}${r.customerId || r.name}${r.since}`} className={s.card} style={{ '--pc': r.project?.color || tint, cursor: r.project ? 'pointer' : 'default' }} onClick={() => (r.project ? navigate(`/projects/${r.project.id}`) : null)}>
                      <div className={s.cardTop}><span className={s.title}>{r.name}</span></div>
                      <div className={s.meta}>
                        {r.project ? <span className={s.proj}><span className="dot" style={{ background: r.project.color || tint }} />{r.project.name}</span> : <span className={s.tag}>no ops job yet</span>}
                      </div>
                      <div className={s.foot}>
                        {r.value ? <span className={s.fact} data-accent>{moneyShort(r.value)}</span> : null}
                        {r.days != null ? <span className={s.fact} data-bad={bad || undefined}><Icon name="clock" size={12} />{r.days} d{col.kind === 'stage' ? ' in pipeline' : col.kind === 'done' ? ' since signed' : ' since lost'}</span> : null}
                        {r.bid?.lastTouch ? <span className={s.fact}><Icon name="phone" size={12} />touched {shortDay(r.bid.lastTouch)}</span> : null}
                        {r.reason ? <span className={s.tag}>{r.reason}</span> : null}
                      </div>
                    </article>
                  );
                })}
                {!col.rows.length ? <div className={s.drop}>{col.hint}</div> : null}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
